import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useSettings } from '../contexts/SettingsContext';

const themes = [
  { id: '', name: 'Blue (Default)', colorClass: 'bg-blue-500' },
  { id: 'theme-green', name: 'Nature Green', colorClass: 'bg-green-500' },
  { id: 'theme-purple', name: 'Royal Purple', colorClass: 'bg-purple-500' },
  { id: 'theme-rose', name: 'Sunset Rose', colorClass: 'bg-rose-500' },
  { id: 'theme-rainbow', name: 'Rainbow (連動カラー)', colorClass: 'bg-gradient-to-r from-rose-300 via-emerald-300 to-sky-300' }
];

const ZAI_MODELS = [
  { value: 'glm-4.7-flash', label: 'GLM-4.7-Flash (高速・軽量)' },
  { value: 'glm-4.7', label: 'GLM-4.7 (バランス)' },
  { value: 'glm-5.2', label: 'GLM-5.2 (高性能)' },
];

const SettingsModal = ({ isOpen, onClose }) => {
  const {
    theme, setTheme,
    mode, setMode,
    apiProvider, setApiProvider,
    model, setModel,
    zaiApiKey, setZaiApiKey,
    openRouterApiKey, setOpenRouterApiKey
  } = useSettings();

  const [localApiProvider, setLocalApiProvider] = useState(apiProvider);
  const [localModel, setLocalModel] = useState(model);
  const [localZaiKey, setLocalZaiKey] = useState(zaiApiKey);
  const [localOpenRouterKey, setLocalOpenRouterKey] = useState(openRouterApiKey);
  const [showZaiKey, setShowZaiKey] = useState(false);
  const [showOrKey, setShowOrKey] = useState(false);

  const handleProviderChange = (newProvider) => {
    setLocalApiProvider(newProvider);
    if (newProvider === 'zai') {
      setLocalModel('glm-4.7-flash');
    } else {
      setLocalModel('');
    }
  };

  const handleSave = () => {
    setApiProvider(localApiProvider);
    setModel(localModel);
    setZaiApiKey(localZaiKey);
    setOpenRouterApiKey(localOpenRouterKey);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 12 }}
          transition={{ type: 'spring', stiffness: 380, damping: 30 }}
          className="glass-panel !bg-white/90 dark:!bg-gray-900/90 rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col"
        >
          <div className="flex items-center justify-between p-5 border-b border-white/70 dark:border-white/10">
            <h2 className="text-lg font-bold flex items-center gap-2 text-gray-900 dark:text-white">
              <SafeIcon icon={FiIcons.FiSettings} className="text-primary-600 dark:text-primary-400" />
              設定 (Settings)
            </h2>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center hover:bg-black/5 dark:hover:bg-white/10 rounded-full text-gray-500 dark:text-gray-400 transition-colors">
              <SafeIcon icon={FiIcons.FiX} className="text-xl" />
            </button>
          </div>

          <div className="p-6 space-y-6 overflow-y-auto flex-1">
            {/* モード設定 */}
            <div>
              <label className="block text-sm font-bold text-gray-800 dark:text-gray-200 mb-3">表示モード</label>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => setMode('light')} className={`flex items-center justify-center gap-2 p-3 rounded-2xl border-2 font-semibold transition-all ${mode === 'light' ? 'border-primary-500 bg-primary-500/10 text-primary-700 dark:text-primary-400' : 'border-gray-300/50 dark:border-white/10 hover:border-gray-400/60 dark:hover:border-white/20 text-gray-700 dark:text-gray-300'}`}>
                  <SafeIcon icon={FiIcons.FiSun} /> ライトモード
                </button>
                <button onClick={() => setMode('dark')} className={`flex items-center justify-center gap-2 p-3 rounded-2xl border-2 font-semibold transition-all ${mode === 'dark' ? 'border-primary-500 bg-primary-500/10 text-primary-700 dark:text-primary-400' : 'border-gray-300/50 dark:border-white/10 hover:border-gray-400/60 dark:hover:border-white/20 text-gray-700 dark:text-gray-300'}`}>
                  <SafeIcon icon={FiIcons.FiMoon} /> ダークモード
                </button>
              </div>
            </div>

            <hr className="border-gray-200/70 dark:border-white/10" />

            {/* テーマ設定 */}
            <div>
              <label className="block text-sm font-bold text-gray-800 dark:text-gray-200 mb-3">カラーテーマ</label>
              <div className="grid grid-cols-2 gap-3">
                {themes.map((t) => (
                  <button key={t.id} onClick={() => setTheme(t.id)} className={`flex items-center gap-3 p-3 rounded-2xl border-2 transition-all ${theme === t.id ? 'border-primary-500 bg-primary-500/10' : 'border-gray-300/50 dark:border-white/10 hover:border-gray-400/60 dark:hover:border-white/20'}`}>
                    <div className={`w-6 h-6 rounded-full ${t.colorClass} shadow-sm`} />
                    <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{t.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <hr className="border-gray-200/70 dark:border-white/10" />

            {/* AI連携設定 */}
            <div>
              <h3 className="text-sm font-bold text-gray-800 dark:text-gray-200 mb-4 flex items-center gap-2">
                <SafeIcon icon={FiIcons.FiCpu} className="text-primary-600 dark:text-primary-400" />
                AI自動生成API設定
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5">使用するAPIプロバイダー</label>
                  <select value={localApiProvider} onChange={(e) => handleProviderChange(e.target.value)} className="glass-input !py-2 text-sm">
                    <option value="zai">Z.ai API</option>
                    <option value="openrouter">OpenRouter API</option>
                  </select>
                </div>

                {/* Z.ai: モデル選択ドロップダウン */}
                {localApiProvider === 'zai' && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5">使用モデル</label>
                    <select value={localModel} onChange={(e) => setLocalModel(e.target.value)} className="glass-input !py-2 text-sm">
                      {ZAI_MODELS.map(m => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* OpenRouter: モデル名手動入力 */}
                {localApiProvider === 'openrouter' && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5">使用モデル (モデル名を入力)</label>
                    <input
                      type="text"
                      value={localModel}
                      onChange={(e) => setLocalModel(e.target.value)}
                      placeholder="openai/gpt-4o-mini"
                      className="glass-input !py-2 text-sm"
                    />
                  </div>
                )}

                {localApiProvider === 'zai' && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5">Z.ai APIキー</label>
                    <div className="relative">
                      <input type={showZaiKey ? 'text' : 'password'} value={localZaiKey} onChange={(e) => setLocalZaiKey(e.target.value)} placeholder="sk-..." className="glass-input !py-2 pr-10 text-sm font-mono" />
                      <button type="button" onClick={() => setShowZaiKey(!showZaiKey)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
                        <SafeIcon icon={showZaiKey ? FiIcons.FiEyeOff : FiIcons.FiEye} />
                      </button>
                    </div>
                  </div>
                )}

                {localApiProvider === 'openrouter' && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5">OpenRouter APIキー</label>
                    <div className="relative">
                      <input type={showOrKey ? 'text' : 'password'} value={localOpenRouterKey} onChange={(e) => setLocalOpenRouterKey(e.target.value)} placeholder="sk-or-v1-..." className="glass-input !py-2 pr-10 text-sm font-mono" />
                      <button type="button" onClick={() => setShowOrKey(!showOrKey)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
                        <SafeIcon icon={showOrKey ? FiIcons.FiEyeOff : FiIcons.FiEye} />
                      </button>
                    </div>
                  </div>
                )}

                <p className="text-xs text-gray-600 dark:text-gray-400 bg-black/5 dark:bg-white/5 p-2.5 rounded-xl font-medium">
                  APIキーはブラウザのローカルストレージにのみ保存され、サーバーには送信されません。
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 p-4 border-t border-white/70 dark:border-white/10">
            <button onClick={onClose} className="px-4 py-2.5 text-sm glass-btn-secondary">キャンセル</button>
            <button onClick={handleSave} className="px-4 py-2.5 text-sm glass-btn-primary">保存して閉じる</button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default SettingsModal;